import { logError } from "./datadog";
import { logger } from "./logger";

/**
 * Guards against `NotFoundError: Failed to execute 'removeChild' on 'Node'`
 * crashes caused by third parties (Google Translate, browser extensions,
 * injected widgets) mutating DOM nodes that React owns.
 *
 * Google Translate is the most common trigger: it wraps text nodes in
 * <font> elements, so when React later tries to remove the original node
 * from the parent it recorded, the call throws and the whole tree crashes
 * to the router's error boundary (see Monday ticket 12736780036 — checkout
 * crash surfaced with a French-translated error page).
 *
 * The guards keep React's contract intact (removeChild returns the child,
 * insertBefore returns the inserted node) and only divert when the DOM has
 * been externally mutated. Every diversion is reported to Datadog so the
 * underlying interference stays visible instead of being silently swallowed.
 */

const MAX_REPORTS_PER_SESSION = 5;
let reportCount = 0;
let installed = false;

const describeNode = (node: Node | null): string => {
  if (!node) return "null";
  if (node.nodeType === Node.TEXT_NODE) return "#text";
  return (node as Element).tagName?.toLowerCase() ?? node.nodeName;
};

const report = (operation: string, child: Node, parent: Node) => {
  try {
    if (reportCount >= MAX_REPORTS_PER_SESSION) return;
    reportCount += 1;

    const context = {
      operation,
      child_node: describeNode(child),
      actual_parent_node: describeNode(child.parentNode),
      expected_parent_node: describeNode(parent),
      // Google Translate wraps text nodes in <font> elements — flag the
      // signature so occurrences are easy to segment in Datadog.
      likely_google_translate: describeNode(child.parentNode) === "font",
      path: window.location.pathname,
    };

    logger.warn(`DOM safety guard prevented a crash (${operation})`, context);
    logError("dom_safety_guard_triggered", undefined, context);
  } catch {
    // Reporting must never throw from inside a DOM operation.
  }
};

export const installDomSafetyGuards = (): void => {
  if (installed || typeof Node !== "function" || !Node.prototype) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      report("removeChild", child, this);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      report("insertBefore", referenceNode, this);
      // The reference node was moved by a third party; append instead so
      // the new content is still rendered rather than dropped.
      return this.appendChild(newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
